import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { BlockBlobClient } from "@azure/storage-blob";
import { useAuth } from "../../context/AuthContext";
import {
  fetchMethodologyDocs,
  requestMethodologyDocSas,
  completeMethodologyDocUpload,
  fetchMethodologyDocDownloadUrl,
  deleteMethodologyDoc,
} from "../../services/RiskMonitorAxios";

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

const CONTENT_TYPES = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

function contentTypeFor(filename) {
  const lower = filename.toLowerCase();
  const extension = ALLOWED_EXTENSIONS.find((ext) => lower.endsWith(ext));
  return CONTENT_TYPES[extension] || "application/octet-stream";
}

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function MethodologyDocButton() {
  const { claims } = useAuth();
  // Same gate as the G-VaR bulk data Upload button — methodology-doc
  // upload/delete is managed by the same people.
  const canManage =
    claims?.roles?.includes("role.risk_upload") ||
    (process.env.NODE_ENV === "development" && process.env.REACT_APP_FORCE_UPLOAD_UI === "true");

  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadDocs = () => {
    setLoading(true);
    setError(null);
    fetchMethodologyDocs()
      .then((data) => setDocs(data || []))
      .catch((err) => {
        console.error("Failed to load methodology docs:", err);
        setError("Failed to load methodology documents.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) loadDocs();
  }, [open]);

  const handleClose = () => {
    if (uploading) return;
    setOpen(false);
    setError(null);
    setSuccess(null);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    if (!ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
      setError("Only PDF or Word documents (.pdf, .doc, .docx) are allowed");
      return;
    }

    const replacing = docs.some((doc) => doc.filename === file.name);
    if (
      replacing &&
      !window.confirm(`${file.name} already exists and will be replaced. Continue?`)
    ) {
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      // Backend mints a short-lived write SAS; the file itself goes straight
      // from the browser to storage, same path as the G-VaR parquet upload.
      const { upload } = await requestMethodologyDocSas(file.name);
      const blobClient = new BlockBlobClient(upload.upload_url);
      await blobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: contentTypeFor(file.name) },
      });
      await completeMethodologyDocUpload(file.name);
      setSuccess(replacing ? `Replaced ${file.name}` : `Uploaded ${file.name}`);
      loadDocs();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      const corsBlocked = /Failed to fetch|CORS|NetworkError/i.test(detail || "");
      setError(
        corsBlocked
          ? `Direct upload to storage was blocked by CORS. In Azure Portal → storage account → Resource sharing (CORS) → Blob service, add this origin (${window.location.origin}), methods GET/PUT/POST/HEAD/OPTIONS, headers *.`
          : `Upload failed: ${detail}`,
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    // Opened synchronously, inside the click, then pointed at the SAS URL once
    // it arrives — a window.open() after the await is treated as an unrequested
    // popup and blocked. Any signed-in user can do this; the SAS is minted by
    // the API, so readers need no storage permissions of their own.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const url = await fetchMethodologyDocDownloadUrl(doc.filename);
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup blocked — fall back to navigating the current tab.
        window.location.href = url;
      }
    } catch (err) {
      if (tab) tab.close();
      setError(`Download failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await deleteMethodologyDoc(doc.filename);
      loadDocs();
    } catch (err) {
      setError(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Backend returns newest first, so the first entry is the current doc.
  const latestFilename = docs.length > 0 ? docs[0].filename : null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DescriptionIcon />}
        onClick={() => setOpen(true)}
        sx={{
          color: "#006842",
          borderColor: "#006842",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { borderColor: "#005233", bgcolor: "rgba(0,104,66,0.06)" },
        }}
      >
        Methodology Doc
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={uploading}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
          <DescriptionIcon sx={{ color: "#006842" }} />
          Methodology Document
          <IconButton
            onClick={handleClose}
            disabled={uploading}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {canManage && (
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" component="label" disabled={uploading} size="small" startIcon={<UploadIcon />}>
                {uploading ? "Uploading..." : "Upload Document"}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Uploading a file with a name already listed below replaces it.
              </Typography>
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Documents
          </Typography>

          {loading && <Typography variant="body2" color="text.secondary">Loading...</Typography>}

          {!loading && docs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No methodology document has been uploaded yet.
            </Typography>
          )}

          {!loading && docs.length > 0 && (
            <List dense>
              {docs.map((doc) => (
                <ListItem key={doc.blob_path || doc.filename} sx={{ px: 0, gap: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {doc.filename}
                        {doc.filename === latestFilename && (
                          <Chip label="Latest" size="small" color="success" />
                        )}
                      </Box>
                    }
                    secondary={[
                      doc.uploaded_by || "unknown",
                      doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : null,
                      formatSize(doc.size_bytes),
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  />
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => handleDownload(doc)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManage && (
                    <Tooltip title="Delete this document">
                      <IconButton size="small" onClick={() => handleDelete(doc)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={uploading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MethodologyDocButton;
